import api from '@/lib/api/axios'
import { toAssetBaseName } from '@/lib/assetFilename'

/**
 * The shape library client.
 *
 * Mirrors features/objects/api/icons.ts, including the two-step upload: the file BYTES go
 * straight to S3 through a short-lived presigned URL, and only the metadata comes back through
 * our API. That is not premature optimisation — it is there because WAF body inspection was
 * intermittently false-blocking binary uploads on the old path, which surfaced as an
 * "Unexpected token '<'" parse error rather than anything an operator could diagnose.
 */

export interface ShapeItem {
    id: number
    filename: string
    /**
     * Which section this shape may appear in — the same strings as a classification's
     * Category. Deliberately NOT a classification: a switch looks like a switch across
     * all nine network classifications, and there are several shapes per category, so
     * the category picks the section and the tags pick the specific thing.
     */
    category: string
    description: string | null
    tags: string | null
    /**
     * The graphic's SIZE at the library's shared scale: the canvas draws it at width x 0.4 by
     * height x 0.4 (shapeBox), and the layout engine reserves that footprint before anything
     * renders, so it has to travel as data. Not the file's pixels since 2026-09-22: a file can be
     * any resolution and still draw 2 cm high, and its resolution is read from the image itself on
     * the screens that show it. Null on a file the browser could not decode; those
     * fall back to a square.
     */
    width: number | null
    height: number | null
}

export interface ShapeUsage {
    shapeId: number
    filename: string
    counts: Record<string, number>
    total: number
    inUse: boolean
    /**
     * Referrers that will REFUSE the delete rather than be cleared — a type table, one row of
     * which renders many objects. Named individually so the dialog can say what to reassign.
     * Empty when nothing blocks.
     */
    blocking: { label: string; count: number }[]
    /**
     * True while no table in the database references shapes at all — the visualisation that will
     * consume them is still in development. Distinguishes "checked, and nothing uses it" from
     * "there is nothing to check against yet", which are very different things to tell someone
     * who is about to delete a file.
     */
    noReferrersConfigured: boolean
}

/**
 * A graphic's NAME — what a person calls it, as opposed to what the file is called.
 *
 * The description is the name; the filename is only where the bytes live. Falls back to the
 * filename with its extension stripped so a graphic uploaded without a description still reads
 * as something rather than as "coreswitch.png".
 *
 * Lives here, not on a screen, because two screens now show it: the library grid and the
 * classification assignment dialog. Two copies would drift, and a name that disagrees with
 * itself between the place you set it and the place you pick it is worse than no name.
 */
export function graphicName(shape: Pick<ShapeItem, 'description' | 'filename'>): string {
    return (shape.description || shape.filename || '').replace(/\.(svg|png|webp|jpe?g|gif)$/i, '')
}

/** The extension a stored graphic keeps, from the file itself or its declared type. */
export function extensionFor(file: File): string {
    const fromName = (file.name.match(/\.[^.]+$/) || [''])[0]
    if (fromName) return fromName.toLowerCase()
    if (file.type === 'image/svg+xml') return '.svg'
    if (file.type === 'image/webp') return '.webp'
    if (file.type === 'image/gif') return '.gif'
    if (file.type === 'image/jpeg') return '.jpg'
    return '.png'
}

/**
 * ONE rule for the stored filename, used by every screen that uploads a graphic and by the
 * preview that promises what the filename will be. A second copy would be worse than showing
 * nothing: the label would be believed, and the two would drift the first time either changed.
 *
 * Identical to the icon rule — lowercase, non-alphanumerics removed rather than hyphenated — so
 * the same typed name produces the same base name on both screens. Only the extension differs,
 * because a graphic keeps its own.
 */
export function graphicFilenameFor(file: File | null, name: string): string {
    return file ? `${toAssetBaseName(name, 'shape')}${extensionFor(file)}` : ''
}

/** Content types the presign endpoint accepts. SVG first: a shape wants to scale. */
export const SHAPE_ACCEPT = 'image/svg+xml,image/png,image/webp,image/jpeg,image/gif'

/**
 * The artwork's intrinsic size, read from the decoded image.
 *
 * Works for SVG too, where naturalWidth/naturalHeight report the viewBox — which is exactly the
 * ratio we want, since an SVG has no pixels of its own.
 *
 * Never throws: an undecodable file is a shape without dimensions, not a failed upload.
 *
 * Exported for the upload form, which reads the same pixels before saving to show what size the
 * file will draw at and how far it stays sharp when zoomed (lib/graphic-resolution.ts).
 */
export async function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    const url = URL.createObjectURL(file)
    try {
        return await new Promise((resolve) => {
            const img = new Image()
            img.onload = () =>
                resolve(
                    img.naturalWidth > 0 && img.naturalHeight > 0
                        ? { width: img.naturalWidth, height: img.naturalHeight }
                        : null,
                )
            img.onerror = () => resolve(null)
            img.src = url
        })
    } finally {
        URL.revokeObjectURL(url)
    }
}

export const shapesApi = {
    list: (search?: string, category?: string) =>
        api.get<{ success: boolean; data: ShapeItem[] }>('/api/shapes', {
            params: {
                ...(search ? { search } : {}),
                ...(category ? { category } : {}),
            },
        }),

    /** The categories a shape can be assigned to, derived from the live classification set. */
    categories: () => api.get<{ success: boolean; data: string[] }>('/api/shapes/categories'),

    create: (data: { filename: string; category?: string; description?: string; tags?: string; overwrite?: boolean }) =>
        api.post<{ success: boolean; data: ShapeItem }>('/api/shapes', data),

    /** Edit category/description/tags, or the size it draws at, without re-uploading the file. */
    update: (id: number, data: { category?: string; description?: string; tags?: string; width?: number; height?: number }) =>
        api.patch<{ success: boolean; data: ShapeItem }>(`/api/shapes/${id}`, data),

    /** Where a shape is referenced — drives the delete warning. */
    usage: (id: number) =>
        api.get<{ success: boolean; data: ShapeUsage }>(`/api/shapes/${id}/usage`),

    /** Delete a shape: clears every reference in the base tables, then removes the row + file. */
    remove: (id: number) =>
        api.delete<{ success: boolean; data: { id: number; filename: string; cleared: Record<string, number>; fileDeleted: boolean } }>(
            `/api/shapes/${id}`,
        ),

    /**
     * Upload the file to S3, then create (or overwrite) the DB record.
     *
     * The SIZE it draws at is NOT sent: the API measures the object it has just been given and
     * decides for itself (routes/api/shapes.js sizeFromStoredFile). A client computes one too, in
     * lib/graphic-resolution.ts, but only to show what the upload will look like — a browser can
     * be running code from before the last deploy, and one that did is how a graphic came to draw
     * at a size nobody chose.
     */
    upload: async (
        file: File, description?: string, tags?: string, overwrite?: boolean, category?: string,
    ): Promise<ShapeItem> => {
        const contentType = file.type || 'application/octet-stream'
        const presign = await api.post<{ uploadUrl: string; key: string; url: string; cacheControl: string }>(
            '/api/images/presign',
            { directory: 'foreground/shapes', imageName: file.name, contentType },
        )

        const putRes = await fetch(presign.data.uploadUrl, {
            method: 'PUT',
            body: file,
            // A presigned PUT signs `host` only — nothing here is enforced, and anything the PUT
            // omits is simply stored empty. Content-Type and Cache-Control are therefore sent
            // because the OBJECT needs them, not because the signature demands them: without the
            // Cache-Control, a replaced shape goes on rendering as the old one for a day.
            // The server's own value is echoed back rather than repeated here, so the two cannot
            // drift apart.
            headers: { 'Content-Type': contentType, 'Cache-Control': presign.data.cacheControl },
        })
        if (!putRes.ok) {
            throw new Error('Failed to upload shape file to storage')
        }

        // A 409 here means a duplicate filename; the caller retries with overwrite: true. The
        // response carries the size the server measured, which is the authoritative one.
        const dbRes = await shapesApi.create({ filename: file.name, category, description, tags, overwrite })
        return dbRes.data.data
    },
}
